import {
  ETAPAS,
  type Etapa,
  EXTENSIONES_ARCHIVO,
  esCodigoValido,
  esquemaImportarHistorial,
  formatearRevision,
  MANEJOS,
  type Manejo,
  NIVEL_PROTECCION,
  NIVELES_PROTECCION,
  type NivelProteccion,
  NOMBRE_ETAPA,
  NOMBRE_MANEJO,
  revisionDelNombre,
  TAMANO_MAXIMO_ARCHIVO,
} from "@gestiondoc/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useRouteContext, useSearch } from "@tanstack/react-router";
import { type FormEvent, useEffect, useId, useState } from "react";
import { queryClient, useTRPC } from "../api.ts";
import { Dropzone } from "../componentes/Dropzone.tsx";
import { Encabezado } from "../componentes/Encabezado.tsx";
import {
  Aviso,
  Boton,
  BotonEnlace,
  Campo,
  CLASE_ENTRADA,
  EstadoVacio,
  Pildora,
  Tarjeta,
} from "../componentes/ui.tsx";
import { formatearTamano, leerComoBase64, mensajeDe, mimeDeArchivo } from "../utiles.ts";

/**
 * Fase 5 · Importar historial (card "Importar historial" de la pantalla 02). Un documento que ya
 * existe en papel entra con todas sus revisiones de una vez, en vez de subirlas una por una desde
 * la Rev. 01 como en CSGestión. Cada revisión: su archivo, la fecha de aprobación del papel, el
 * motivo del cambio y quién la firmó en papel. La última queda vigente; las anteriores, obsoletas.
 */

type Firma = { nombre: string; puesto: string };
type Fila = {
  clave: number;
  numero: string;
  archivo: File | null;
  aprobadaEl: string;
  motivo: string;
  firmas: Record<Etapa, Firma>;
  /** Ya subido para este código y esta revisión (si cambian, se vuelve a subir). */
  subido: { archivoId: string; para: string } | null;
};

const SIN_FIRMAS: Record<Etapa, Firma> = {
  elaboro: { nombre: "", puesto: "" },
  reviso: { nombre: "", puesto: "" },
  aprobo: { nombre: "", puesto: "" },
};

let siguienteClave = 1;

/**
 * Para validar el formulario antes de subir nada hace falta un id de archivo con forma de UUID.
 * Fijo, y no `crypto.randomUUID()`: esa función no existe cuando la app se abre por http con la
 * IP de la planta (solo en https y localhost), y el botón quedaba sin hacer nada en la tablet.
 */
const ID_DE_MUESTRA = "00000000-0000-4000-8000-000000000000";

const porNumero = (a: Fila, b: Fila) => (Number(a.numero) || 0) - (Number(b.numero) || 0);

export function PaginaImportarHistorial() {
  const { perfil } = useRouteContext({ from: "/app" });
  const search = useSearch({ from: "/app/historial/importar" });
  const trpc = useTRPC();
  const navigate = useNavigate();

  const [codigo, setCodigo] = useState(search.proceso ? `${search.proceso}-` : "");
  const [nombre, setNombre] = useState("");
  const [manejo, setManejo] = useState<Manejo>("digital");
  const [nivel, setNivel] = useState<NivelProteccion>(2);
  const [retencion, setRetencion] = useState("24");
  const [justificacion, setJustificacion] = useState("");
  const [filas, setFilas] = useState<Fila[]>([]);
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [aviso, setAviso] = useState<string | null>(null);
  const [progreso, setProgreso] = useState<string | null>(null);

  const codigoNormal = codigo.trim().toUpperCase();
  const revisado = useQuery({
    ...trpc.historial.revisarCodigo.queryOptions({ codigo: codigoNormal }),
    enabled: esCodigoValido(codigoNormal) && perfil.usuario.rolGlobal === "administrador",
  });
  const subir = useMutation(trpc.historial.subirArchivo.mutationOptions());
  const importar = useMutation(trpc.historial.importar.mutationOptions());

  if (perfil.usuario.rolGlobal !== "administrador") {
    return <EstadoVacio>Solo un administrador importa el historial de un documento.</EstadoVacio>;
  }

  const esRegistro = revisado.data?.valido && revisado.data.esRegistro;
  // Las filas se muestran en el orden en que están y se ordenan al salir del campo "Rev." (y al
  // importar): ordenarlas con cada tecla movía la fila mientras se escribía el número.
  const ordenar = () => setFilas((fs) => [...fs].sort(porNumero));

  function cambiar(clave: number, cambio: Partial<Fila>) {
    setFilas((fs) => fs.map((f) => (f.clave === clave ? { ...f, ...cambio } : f)));
  }

  /** Cada archivo, una revisión; el número sale del nombre o sigue al mayor. */
  function agregarArchivos(archivos: File[]) {
    setFilas((fs) => {
      const nuevas = [...fs];
      for (const archivo of archivos) {
        const usados = new Set(nuevas.map((f) => Number(f.numero)));
        const delNombre = revisionDelNombre(archivo.name);
        const numero =
          delNombre && !usados.has(delNombre)
            ? delNombre
            : Math.max(0, ...nuevas.map((f) => Number(f.numero) || 0)) + 1;
        nuevas.push(filaNueva(nuevas, numero, archivo));
      }
      return nuevas.sort(porNumero);
    });
  }

  function agregarVacia() {
    setFilas((fs) => [
      ...fs,
      filaNueva(fs, Math.max(0, ...fs.map((f) => Number(f.numero) || 0)) + 1, null),
    ]);
  }

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (progreso !== null) return;
    setAviso(null);
    const ordenadas = [...filas].sort(porNumero);
    setFilas(ordenadas);
    const nuevos: Record<string, string> = {};
    /** Los errores van con la clave de la fila, no con su posición: la fila puede moverse. */
    const deFila = (i: number, campo: string) => `${ordenadas[i]?.clave}.${campo}`;
    // Primero se valida todo con ids de mentira: no se sube nada si el formulario está mal.
    const comoQuedaria = (ids: string[]) => ({
      codigo: codigoNormal,
      nombre,
      manejo,
      justificacion,
      nivelProteccion: nivel,
      retencionMeses: Number(retencion),
      revisiones: ordenadas.map((f, i) => ({
        numero: Number(f.numero),
        archivoId: ids[i] ?? "",
        aprobadaEl: f.aprobadaEl,
        motivo: f.motivo,
        firmas: f.firmas,
      })),
    });
    ordenadas.forEach((f, i) => {
      if (!f.archivo) nuevos[deFila(i, "archivoId")] = "Adjunta el archivo de esta revisión";
      else if (!mimeDeArchivo(f.archivo))
        nuevos[deFila(i, "archivoId")] = "Solo se aceptan Word, Excel o PDF";
      else if (f.archivo.size > TAMANO_MAXIMO_ARCHIVO)
        nuevos[deFila(i, "archivoId")] = "El archivo supera los 25 MB";
    });
    const prueba = esquemaImportarHistorial.safeParse(
      comoQuedaria(ordenadas.map(() => ID_DE_MUESTRA)),
    );
    /** Lo que no tiene un campo donde mostrarse se dice arriba, para que nada falle en silencio. */
    const sinLugar: string[] = [];
    if (!prueba.success) {
      for (const p of prueba.error.issues) {
        const [raiz, i, ...resto] = p.path;
        let campo: string;
        if (raiz === "revisiones" && typeof i === "number") {
          // Un error dentro de las firmas (un nombre demasiado largo) se muestra bajo ellas.
          campo = deFila(i, resto[0] === "firmas" ? "firmas" : resto.join("."));
        } else {
          campo = p.path.join(".");
          if (!CAMPOS_CON_LUGAR.has(campo)) sinLugar.push(p.message);
        }
        if (!nuevos[campo]) nuevos[campo] = p.message;
      }
    }
    if (revisado.data && !revisado.data.valido) nuevos.codigo = revisado.data.motivo;
    setErrores(nuevos);
    if (Object.keys(nuevos).length > 0) {
      setAviso(["Revisa los campos marcados.", ...sinLugar].join(" "));
      return;
    }

    try {
      const ids: string[] = [];
      for (const [i, f] of ordenadas.entries()) {
        const para = `${codigoNormal}|${f.numero}`;
        if (f.subido?.para === para) {
          ids.push(f.subido.archivoId);
          continue;
        }
        const archivo = f.archivo;
        const mime = archivo ? mimeDeArchivo(archivo) : null;
        if (!archivo || !mime) throw new Error("Falta un archivo");
        setProgreso(`Subiendo el archivo ${i + 1} de ${ordenadas.length}…`);
        const r = await subir.mutateAsync({
          codigo: codigoNormal,
          revision: Number(f.numero),
          archivo: { nombre: archivo.name, mime, base64: await leerComoBase64(archivo) },
        });
        cambiar(f.clave, { subido: { archivoId: r.archivoId, para } });
        ids.push(r.archivoId);
      }
      setProgreso("Importando…");
      const datos = esquemaImportarHistorial.parse(comoQuedaria(ids));
      const r = await importar.mutateAsync(datos);
      queryClient.invalidateQueries();
      navigate({ to: "/documentos/$codigo", params: { codigo: r.codigo }, search: {} });
    } catch (err) {
      setAviso(mensajeDe(err));
    } finally {
      setProgreso(null);
    }
  }

  const ocupado = progreso !== null;
  const ultima = [...filas].sort(porNumero).at(-1);
  return (
    <form onSubmit={(e) => void enviar(e)} noValidate className="max-w-[1080px]">
      <NoAbrirArchivosSoltados />
      <Encabezado
        overline="Documentos › Importar historial"
        titulo="Importar historial"
        acciones={
          <>
            {search.proceso ? (
              <BotonEnlace
                to="/procesos/$codigo"
                params={{ codigo: search.proceso }}
                variante="discreto"
              >
                Cancelar
              </BotonEnlace>
            ) : (
              <BotonEnlace to="/" variante="discreto">
                Cancelar
              </BotonEnlace>
            )}
            <Boton type="submit" disabled={ocupado || filas.length === 0}>
              {progreso ??
                (filas.length > 1 ? `Importar ${filas.length} revisiones` : "Importar historial")}
            </Boton>
          </>
        }
      />
      <p className="mb-4 max-w-[760px] text-[13px] text-texto-2">
        Para un documento que ya existe en papel con varias revisiones: súbelas todas de una vez, de
        la más antigua a la vigente, con la fecha en que se aprobó cada una y quién la firmó. La
        última queda vigente; las anteriores, obsoletas. Ninguna pasa por las firmas de la app: sus
        firmas están en el papel.
      </p>
      {aviso && <Aviso className="mb-4">{aviso}</Aviso>}

      {/* Mientras suben los archivos no se edita: lo que se cambie ya no entraría en la importación. */}
      <fieldset disabled={ocupado} className="m-0 min-w-0 border-0 p-0 disabled:opacity-70">
        <Tarjeta titulo="El documento" className="mb-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              etiqueta="Código"
              ayuda={
                revisado.data?.valido
                  ? `${revisado.data.proceso} · ${revisado.data.tipo}`
                  : "Como dice el papel, ej. LOG-PRO-005"
              }
              error={
                errores.codigo ??
                (revisado.data?.valido === false ? revisado.data.motivo : undefined)
              }
            >
              <input
                className={`${CLASE_ENTRADA} font-mono uppercase`}
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                placeholder="LOG-PRO-005"
                autoComplete="off"
              />
            </Campo>
            <Campo etiqueta="Nombre" error={errores.nombre}>
              <input
                className={CLASE_ENTRADA}
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Procedimiento de despacho a sucursales"
              />
            </Campo>
            <Campo etiqueta="Manejo">
              <select
                className={CLASE_ENTRADA}
                value={manejo}
                onChange={(e) => setManejo(e.target.value as Manejo)}
              >
                {MANEJOS.map((m) => (
                  <option key={m} value={m}>
                    {NOMBRE_MANEJO[m]}
                  </option>
                ))}
              </select>
            </Campo>
            <Campo etiqueta="Nivel de protección" ayuda={NIVEL_PROTECCION[nivel].quienVe}>
              <select
                className={CLASE_ENTRADA}
                value={nivel}
                onChange={(e) => setNivel(Number(e.target.value) as NivelProteccion)}
              >
                {NIVELES_PROTECCION.map((n) => (
                  <option key={n} value={n}>
                    {n} · {NIVEL_PROTECCION[n].nombre}
                  </option>
                ))}
              </select>
            </Campo>
            {esRegistro && (
              <Campo
                etiqueta="Se conserva (meses)"
                ayuda="Retención de cada registro lleno"
                error={errores.retencionMeses}
              >
                <input
                  className={CLASE_ENTRADA}
                  type="number"
                  min={1}
                  max={600}
                  value={retencion}
                  onChange={(e) => setRetencion(e.target.value)}
                />
              </Campo>
            )}
            <div className="sm:col-span-2">
              <Campo
                etiqueta="Justificación"
                ayuda="Opcional: para qué existe el documento."
                error={errores.justificacion}
              >
                <textarea
                  className={CLASE_ENTRADA}
                  rows={2}
                  value={justificacion}
                  onChange={(e) => setJustificacion(e.target.value)}
                />
              </Campo>
            </div>
          </div>
        </Tarjeta>

        <Tarjeta
          titulo="Revisiones"
          ayuda="Puedes soltar todos los archivos juntos: el número de revisión se toma del nombre (Rev03, R3…). Revísalo antes de importar."
        >
          <Dropzone
            multiple
            onArchivos={agregarArchivos}
            texto="Arrastra aquí los archivos de todas las revisiones o haz clic para elegirlos"
          />
          {errores.revisiones && (
            <div className="mt-2 text-[11.5px] text-rojo">{errores.revisiones}</div>
          )}
          <ol className="mt-4 space-y-4">
            {filas.map((f) => (
              <FilaDeRevision
                key={f.clave}
                fila={f}
                vigente={f.clave === ultima?.clave}
                errores={errores}
                onOrdenar={ordenar}
                onCambio={(c) => cambiar(f.clave, c)}
                onQuitar={() => setFilas((fs) => fs.filter((x) => x.clave !== f.clave))}
              />
            ))}
          </ol>
          <button
            type="button"
            onClick={agregarVacia}
            className="mt-4 text-[12.5px] font-semibold text-marino hover:underline"
          >
            + Agregar revisión
          </button>
        </Tarjeta>
      </fieldset>

      {ultima && (
        <p className="mt-4 text-[12.5px] text-texto-3">
          Se creará {codigoNormal || "el documento"} con {filas.length}{" "}
          {filas.length === 1 ? "revisión" : "revisiones"}: la Rev. {numeroLegible(ultima.numero)}{" "}
          queda vigente
          {filas.length > 1 ? " y las anteriores, obsoletas" : ""}. La auditoría registra la
          importación completa.
        </p>
      )}
    </form>
  );
}

/** Fila nueva con las firmas de la revisión anterior: suelen firmar las mismas personas. */
function filaNueva(filas: Fila[], numero: number, archivo: File | null): Fila {
  const anterior = [...filas].sort((a, b) => Number(a.numero) - Number(b.numero)).at(-1);
  return {
    clave: siguienteClave++,
    numero: String(numero),
    archivo,
    aprobadaEl: "",
    motivo: filas.length === 0 ? "Emisión inicial" : "",
    firmas: anterior ? structuredClone(anterior.firmas) : structuredClone(SIN_FIRMAS),
    subido: null,
  };
}

function FilaDeRevision({
  fila,
  vigente,
  errores,
  onOrdenar,
  onCambio,
  onQuitar,
}: {
  fila: Fila;
  vigente: boolean;
  errores: Record<string, string>;
  onOrdenar: () => void;
  onCambio: (c: Partial<Fila>) => void;
  onQuitar: () => void;
}) {
  const e = (campo: string) => errores[`${fila.clave}.${campo}`];
  const id = useId();
  return (
    <li className="rounded-tarjeta border border-borde p-4">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-1.5 text-[13px] font-semibold text-marino">
          Rev.
          <input
            type="number"
            min={1}
            max={99}
            value={fila.numero}
            onChange={(ev) => onCambio({ numero: ev.target.value })}
            onBlur={onOrdenar}
            aria-label="Número de revisión"
            className={`${CLASE_ENTRADA} w-[72px] font-mono`}
          />
        </label>
        <Pildora tono={vigente ? "bg-verde-fondo text-verde" : "bg-gris-fondo text-gris"}>
          {vigente ? "Quedará vigente" : "Obsoleta"}
        </Pildora>
        <div className="min-w-0 flex-1 text-[12.5px]">
          <input
            id={id}
            type="file"
            className="sr-only"
            accept={EXTENSIONES_ARCHIVO.map((x) => `.${x}`).join(",")}
            onChange={(ev) => {
              const archivo = ev.target.files?.[0];
              if (archivo) onCambio({ archivo, subido: null });
            }}
          />
          {fila.archivo ? (
            <span className="text-texto-2">
              <span className="font-semibold text-marino">{fila.archivo.name}</span> ·{" "}
              {formatearTamano(fila.archivo.size)} ·{" "}
              <label htmlFor={id} className="cursor-pointer underline">
                cambiar
              </label>
            </span>
          ) : (
            <label htmlFor={id} className="cursor-pointer font-semibold text-marino underline">
              Elegir el archivo de esta revisión
            </label>
          )}
          {e("archivoId") && <div className="text-[11.5px] text-rojo">{e("archivoId")}</div>}
          {e("numero") && <div className="text-[11.5px] text-rojo">{e("numero")}</div>}
        </div>
        <button
          type="button"
          onClick={onQuitar}
          className="text-[12px] font-semibold text-texto-3 hover:text-rojo"
        >
          Quitar
        </button>
      </div>
      <div className="grid gap-3 sm:grid-cols-[180px_1fr]">
        <Campo etiqueta="Aprobada el" error={e("aprobadaEl")}>
          <input
            type="date"
            className={CLASE_ENTRADA}
            value={fila.aprobadaEl}
            onChange={(ev) => onCambio({ aprobadaEl: ev.target.value })}
          />
        </Campo>
        <Campo etiqueta="Motivo del cambio" error={e("motivo")}>
          <input
            className={CLASE_ENTRADA}
            value={fila.motivo}
            onChange={(ev) => onCambio({ motivo: ev.target.value })}
            placeholder="Cambio de responsable de bodega"
          />
        </Campo>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        {ETAPAS.map((etapa) => (
          <fieldset key={etapa}>
            <legend className="mb-1 text-[10.5px] font-semibold tracking-[.08em] text-texto-3 uppercase">
              {NOMBRE_ETAPA[etapa]} · en papel
            </legend>
            <input
              className={CLASE_ENTRADA}
              value={fila.firmas[etapa].nombre}
              onChange={(ev) =>
                onCambio({
                  firmas: {
                    ...fila.firmas,
                    [etapa]: { ...fila.firmas[etapa], nombre: ev.target.value },
                  },
                })
              }
              placeholder="Nombre"
              aria-label={`${NOMBRE_ETAPA[etapa]}: nombre`}
            />
            <input
              className={`${CLASE_ENTRADA} mt-1.5 text-[12px]`}
              value={fila.firmas[etapa].puesto}
              onChange={(ev) =>
                onCambio({
                  firmas: {
                    ...fila.firmas,
                    [etapa]: { ...fila.firmas[etapa], puesto: ev.target.value },
                  },
                })
              }
              placeholder="Puesto (opcional)"
              aria-label={`${NOMBRE_ETAPA[etapa]}: puesto`}
            />
          </fieldset>
        ))}
      </div>
      {e("firmas") && <div className="mt-1 text-[11.5px] text-rojo">{e("firmas")}</div>}
    </li>
  );
}

/** Los campos de "El documento" que muestran su propio error. */
const CAMPOS_CON_LUGAR = new Set([
  "codigo",
  "nombre",
  "retencionMeses",
  "justificacion",
  "revisiones",
]);

/**
 * Un archivo soltado fuera del recuadro hace que el navegador lo abra y se pierda todo lo
 * llenado. Mientras esta pantalla está abierta, soltar fuera no hace nada.
 */
function NoAbrirArchivosSoltados() {
  useEffect(() => {
    const frenar = (e: globalThis.DragEvent) => e.preventDefault();
    window.addEventListener("dragover", frenar);
    window.addEventListener("drop", frenar);
    return () => {
      window.removeEventListener("dragover", frenar);
      window.removeEventListener("drop", frenar);
    };
  }, []);
  return null;
}

/** "3" → "03"; lo que todavía no es un número válido se muestra tal cual, sin romper la página. */
function numeroLegible(numero: string): string {
  const n = Number(numero);
  return Number.isInteger(n) && n >= 1 && n <= 99 ? formatearRevision(n) : numero || "?";
}
